/**
 * usePhotoLikes Hook
 * 
 * Manages likes for a photo.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { PhotoLike } from "../types";

interface UsePhotoLikesReturn {
  likes: PhotoLike[];
  likesCount: number;
  isLiked: boolean;
  isLoading: boolean;
  toggleLike: () => Promise<boolean>;
}

/**
 * Hook to manage photo likes
 */
export const usePhotoLikes = (photoId: string): UsePhotoLikesReturn => {
  const { user, activeProfile } = useAuth();
  const userId = activeProfile?.id || user?.id;
  
  const [likes, setLikes] = useState<PhotoLike[]>([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch likes for the photo
   */
  const fetchLikes = useCallback(async () => {
    if (!photoId) return;

    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("photo_likes")
        .select(`
          *,
          user:profiles(id, full_name, nickname, avatar_url)
        `)
        .eq("photo_id", photoId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setLikes(data || []);
      setIsLiked(data?.some(like => like.user_id === userId) || false);
    } catch (err) {
      console.error("Error fetching likes:", err);
    } finally {
      setIsLoading(false);
    }
  }, [photoId, userId]);

  /**
   * Toggle like on the photo
   */
  const toggleLike = useCallback(async (): Promise<boolean> => {
    if (!userId || !photoId) return false;

    try {
      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from("photo_likes")
          .delete()
          .eq("photo_id", photoId)
          .eq("user_id", userId);

        if (error) throw error;

        setLikes(prev => prev.filter(like => like.user_id !== userId));
        setIsLiked(false);
      } else {
        // Like
        const { data, error } = await supabase
          .from("photo_likes")
          .insert({ photo_id: photoId, user_id: userId })
          .select(`
            *,
            user:profiles(id, full_name, nickname, avatar_url)
          `)
          .single();

        if (error) throw error;

        setLikes(prev => [data, ...prev]);
        setIsLiked(true);
      }

      return true;
    } catch (err) {
      console.error("Error toggling like:", err);
      return false;
    }
  }, [photoId, userId, isLiked]);

  // Fetch on mount
  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  return {
    likes,
    likesCount: likes.length,
    isLiked,
    isLoading,
    toggleLike,
  };
};

